<template>
  <div
    v-if="!isSelectedOptionsEmpty"
    :class="[
      'product-options',
      {
        'product-options--second-turn': isSecondTurn,
        'product-options--contain-badges': showBadges
      }
    ]"
  >
    <!-- Loop over the customizedOptions instead of product.options -->
    <template v-for="(group, optionName) in customizedOptions" :key="optionName">
      <div class="product-options__badge-container" v-if="showBadges">
        <OptionBadge :optionName="optionName" :selectedValue="selectedOptions[optionName]" :group="group"/>
      </div>

      <div v-else class="product-options__option">
        <div class="product-options__title">

          <!-- Selected/Unselected option title -->
          <span>{{ optionName.toUpperCase() }} -</span>
          <div class="product-options__title--selected">
            <span v-if="selectedOptions[optionName]">{{ selectedOptions[optionName] }}</span>
            <span v-else>Select {{ optionName }}</span>
          </div>

          <!-- Size guide -->
          <button
            v-if="optionName === 'Size' && sizeGuideJson && !isSecondTurn"
            class="product-options__size-guide"
            tabindex="0"
            @click="toggleSizeDrawer"
          >
            Size Guide
          </button>
        </div>

        <div :class="getClassForOption(optionName)">
          <!-- Loop through price groups for the Color option -->
          <ColorOptions
            v-if="optionName === 'Color'"
            :group="group"
            :optionName="optionName"
            :selectedOptions="selectedOptions"
            :hideUnavailableOption="hideUnavailableOption"
            :isOptionSeperator="isOptionSeperator"
            :updateSelection="updateSelection"
            :formatPrice="formatPrice"
            :getModifiedOptionName="getModifiedOptionName"
            :isImageTypeSwatch="isImageTypeSwatch"
          />

          <!-- Loop through other options (like Size, Inseam, etc.) that are NOT grouped by price -->
          <OtherOptions
            v-else
            :group="group"
            :optionName="optionName"
            :selectedOptions="selectedOptions"
            :hideUnavailableOption="hideUnavailableOption"
            :additionalPropertyOptions="additionalPropertyOptions"
            :updateSelection="updateSelection"
            :getModifiedOptionName="getModifiedOptionName"
            :seamHandle="seamHandle"
            :isDropdownView="isGiftCard"
          />
        </div>
      </div>
    </template>

    <button
      v-if="Object.keys(customizedOptions).includes('Size') && sizeGuideJson && isSecondTurn"
      class="product-options__size-guide"
      tabindex="0"
      @click="toggleSizeDrawer"
    >
      Size Guide
    </button>
  </div>

  <div v-if="isBIS" class="product-options__bis-notify">Select a size to be notified when it's back in stock.</div>
</template>

<script setup>
  import { computed, toRefs, ref, onMounted, reactive, onUnmounted } from 'vue';
  import { debounce } from 'lodash';
  import { useStore } from 'vuex';
  import ColorOptions from './color-options.vue';
  import OtherOptions from './other-options.vue';
  import OptionBadge from './option-badge.vue';

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({}),
    },
    selectedOptions: {
      type: Object,
      default: () => ({}),
    },
    isSecondTurn: {
      type: Boolean,
      default: false,
    },
    hideColorOption: {
      type: Boolean,
      default: false,
    },
    hideUnavailableOption: {
      type: Boolean,
      default: false,
    },
    isImageTypeSwatch: {
      type: Boolean,
      default: true,
    },
  });

  const { product, selectedOptions, isSecondTurn, hideUnavailableOption, hideColorOption, isImageTypeSwatch } =
    toRefs(props);

  const store = useStore();
  const customerDiscountVal = ref(0);
  const actualProductOptionNames = ref(product.value.options.map((option) => option.name));
  const firstOptionName = ref(product.value.options?.[0]?.name);

  const emit = defineEmits(['on-option-change', 'customized-options-changed']);

  onMounted(async () => {
    handleCustomerDiscount();
  });

  const isSelectedOptionsEmpty = computed(() => {
    return Object.keys(selectedOptions.value).length === 0;
  });

  const isGiftCard = computed(() => product.value.title.includes('E-Gift Card'));

  const isOptionSeperator = computed(() => {
    return !['Stio Classic Mountain Studio Tee', 'E-Gift Card'].includes(product.value.title);
  });

  const sizeGuideJson = computed(() => {
    return product.value.size_guide_json.find((item) => {
      let itemTags = item.tags.split(',').map(tag => tag.trim());
      return product.value.tags.some((productTag) => itemTags.includes(productTag));
    });
  });

  const additionalPropertyOptions = computed(() => {
    const { additional_property_options } = product.value;
    return additional_property_options.length ? additional_property_options.map((adntlOpt) => adntlOpt.value): [];
  });

  // Main computed property to generate customized options
  const customizedOptions = computed(() => {
    const firstOptionSelectedValue = selectedOptions.value[firstOptionName.value]; // Currently selected value for the first option
    const productTags = product.value?.tags;
    const groupedOptions = {};
    const remainingOptionNames = actualProductOptionNames.value.filter((option) => option !== firstOptionName.value);
    let availableSeams = actualProductOptionNames.value?.[2] ? product.value.options?.[2] : undefined;
    // Filter variants based on first option selection
    const filteredVariants =
      actualProductOptionNames.value.length > 1 && firstOptionSelectedValue
        ? product.value.variants.filter((variant) => variant.option1 === firstOptionSelectedValue)
        : product.value.variants;

    if (availableSeams?.values?.length) {
      // Define the preferred order
      const seamOrder = ['Regular', 'Short', 'Long'];
      // Sort the availableSeams based on the preferred order
      availableSeams = seamOrder.filter((item) => availableSeams.values.includes(item));
    }

    refreshSelectedOptions(filteredVariants, remainingOptionNames);

    let modifiedSelectedOption = JSON.parse(JSON.stringify(selectedOptions.value));
    if (actualProductOptionNames.value?.[2]) {
      modifiedSelectedOption = {
        ...modifiedSelectedOption,
        ...(modifiedSelectedOption[actualProductOptionNames.value?.[2]]
          ? { [actualProductOptionNames.value[2]]: modifiedSelectedOption[actualProductOptionNames.value?.[2]] }
          : { [actualProductOptionNames.value[2]]: availableSeams[0] }),
      };
    }

    const groupedFirstOptions = generateFirstOption(
      product.value.variants,
      productTags,
      modifiedSelectedOption,
      remainingOptionNames
    );

    if (firstOptionName.value === 'Color') {
      // Handle 'Color' logic with edition type and price grouping
      console.log('Processing Color grouping with edition type and price', processColorGrouping(groupedFirstOptions));
      
      groupedOptions[firstOptionName.value] = processColorGrouping(groupedFirstOptions);

      // // Handle 'Color' logic (group by price)
      // groupedOptions[firstOptionName.value] = Object.entries(groupedFirstOptions)
      //   .sort(([priceA], [priceB]) => parseFloat(priceB) - parseFloat(priceA)) // Sort by price in descending order
      //   .map(([price, colors]) => ({
      //     price: parseFloat(price),
      //     options: colors,
      //   }));
    } else {
      groupedOptions[firstOptionName.value] = Object.values(groupedFirstOptions).flatMap((item) => item);
    }
    // Handle remaining options logic
    const remainingGroupedOptions = generateRemainingOptions(
      remainingOptionNames,
      modifiedSelectedOption,
      filteredVariants
    );

    // Merge remaining options with groupedOptions
    Object.assign(groupedOptions, remainingGroupedOptions);

    // Merge additional property options (Inseam/Outseam) if present
    ['Inseam', 'Outseam'].forEach((seamProperty) => {
      if (groupedOptions[seamProperty] && additionalPropertyOptions.value.length) {
        // Merge new additional property options into existing options
        const newOptions = additionalPropertyOptions.value.map((item) => ({ value: item }));
        groupedOptions[seamProperty] = [...newOptions];
      }
    });

    return groupedOptions;
  });

  // New function to process color grouping with edition type and price
  const processColorGrouping = (groupedFirstOptions) => {
    // Check if any variant has edition_type metafield
    const hasEditionTypes = product.value.variants.some(variant => 
      variant?.edition_type
    );

    if (hasEditionTypes) {
      return processEditionTypeGrouping(groupedFirstOptions);
    } else {
      // Existing price grouping logic
      return Object.entries(groupedFirstOptions)
        .sort(([priceA], [priceB]) => parseFloat(priceB) - parseFloat(priceA))
        .map(([price, colors]) => ({
          price: parseFloat(price),
          options: colors,
        }));
    }
  };

    // New function to handle edition type grouping
  const processEditionTypeGrouping = (groupedFirstOptions) => {
    const editionGroups = {};
    const priceGroups = {};

    // First, organize all variants by edition type and price
    product.value.variants.forEach(variant => {
      const editionType = variant?.edition_type ? toTitleCase(variant.edition_type)?.trim() : null;

      let price = Number(variant.price / 100);
      const compare_at_price = Number(variant.compare_at_price / 100);
      
      const discountedPrice = getDiscountedPrice(price, compare_at_price);
      const actualPrice = getActualPrice(price, compare_at_price);
      
      let priceKey = price;

      // Avoid grouping on basis of different prices when discount is available
      if (customerDiscountVal.value > 0 && discountedPrice != actualPrice) {
        priceKey = discountedPrice;
      }

      
      if (editionType) {        
        if (!editionGroups[editionType]) {
          editionGroups[editionType] = {};
        }
        if (!editionGroups[editionType][priceKey]) {
          editionGroups[editionType][priceKey] = [];
        }
      } else {
        if (!priceGroups[priceKey]) {
          priceGroups[priceKey] = [];
        }
      }
    });
    
    // Now populate the groups with the color options
    Object.entries(groupedFirstOptions).forEach(([price, colors]) => {
      colors.forEach(colorOption => {
        // Find the variant for this color option
        const matchingVariant = product.value.variants.find(variant => 
          variant.option1 === colorOption.value && getPriceKey(variant) == price
        );

        if (matchingVariant) {
          const editionType = matchingVariant?.edition_type ? toTitleCase(matchingVariant.edition_type)?.trim() : null;
          
          if (editionType) {
            if (!editionGroups[editionType][price]) {
              editionGroups[editionType][price] = [];
            }
            editionGroups[editionType][price].push(colorOption);
          } else {
            if (!priceGroups[price]) {
              priceGroups[price] = [];
            }
            priceGroups[price].push(colorOption);
          }
        }
      });
    });
    
    // Clean up empty price groups in regular priceGroups
    Object.keys(priceGroups).forEach(priceKey => {
      if (priceGroups[priceKey].length === 0) {
        delete priceGroups[priceKey];
      }
    });
    
    const result = [];
    
    // Process edition type groups first
    Object.entries(editionGroups).forEach(([editionType, priceGroups]) => {      
      const sortedPriceGroups = Object.entries(priceGroups)
        .sort(([priceA], [priceB]) => parseFloat(priceB) - parseFloat(priceA))
        .map(([price, colors]) => {
          if (colors.length === 0) {
            return null; // Skip empty groups
          }
          return {
            price: parseFloat(price),
            options: colors,
          };
        }).filter(Boolean);
      
      if (sortedPriceGroups.length === 0) {
        return; // Skip if no price groups
      }

      result.push({
        editionType,
        priceGroups: sortedPriceGroups,
      });
    });

    // Then process regular price groups (variants without edition type)
    if (Object.keys(priceGroups).length > 0) {
      const sortedPriceGroups = Object.entries(priceGroups)
        .sort(([priceA], [priceB]) => parseFloat(priceB) - parseFloat(priceA))
        .map(([price, colors]) => ({
          price: parseFloat(price),
          options: colors,
        }));

      result.push(...sortedPriceGroups);
    }
    
    return result;
  };

   // Helper function to get price key for variant (same logic as in generateFirstOption)
  const getPriceKey = (variant) => {
    let price = Number(variant.price / 100);
    const compare_at_price = Number(variant.compare_at_price / 100);
    
    const discountedPrice = getDiscountedPrice(price, compare_at_price);
    const actualPrice = getActualPrice(price, compare_at_price);
    
    let priceKey = price;

    // Avoid grouping on basis of different prices when discount is available
    if (customerDiscountVal.value > 0 && discountedPrice != actualPrice) {
      priceKey = discountedPrice;
    }

    return priceKey;
  };

  const toTitleCase = (str) => {
    return str.toLowerCase().split(' ').map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }

  // Hardcoding it false, as per Janet we do not need to show badges even if single option is present, but keeping the code In case if we need in future.
  const showBadges = computed(() => {
      // return !Object.entries(customizedOptions.value).some(([, group]) => group.length > 1 || group[0]?.options?.length > 1)
      return false;
    }
  );

  // Helper function to generate the very first option
  const generateFirstOption = (variants, productTags, modifiedSelectedOption, remainingOptionNames) => {
    const groupedFirstOption = {};

    variants.forEach((variant) => {
      let { price, compare_at_price, option1, featured_image: image } = variant;
      price = Number(price / 100);
      compare_at_price = Number(compare_at_price / 100);

      const imagePattern = new RegExp(`^\\s*${variant.option1?.toLowerCase()}\\s*swatch\\s*$`);
      if (firstOptionName.value === 'Color' && isImageTypeSwatch.value) {
        image = product.value.images.find((img) => {
          const alt = img.alt.toLowerCase();
          return alt && imagePattern.test(alt);
        })?.src;
      }

      const bisData = productTags.filter((tag) => tag.toLowerCase().includes(`bis: ${option1.toLowerCase()}`));

      // Check if all remaining options are selected
      const allOptionsSelected = remainingOptionNames.every((optionName) => selectedOptions.value[optionName]);

      // Check availability based on the selected options
      const isMatchingOptions = remainingOptionNames.every((optionName, idx) => {
        const selectedOption = modifiedSelectedOption[optionName];
        return !selectedOption || variant[`option${idx + 2}`] === selectedOption;
      });

      if (!product.value.available || variant.available || bisData.length) {
        const isAvailable = (!allOptionsSelected || isMatchingOptions) && variant.available;
        const discountedPrice = getDiscountedPrice(price, compare_at_price);
        const actualPrice = getActualPrice(price, compare_at_price);

        let priceKey = price;

        // Avoid grouping on basis of different prices when discount is available
        if (customerDiscountVal.value > 0 && discountedPrice != actualPrice) {
          priceKey = discountedPrice;
        }

        if (!groupedFirstOption[priceKey]) groupedFirstOption[priceKey] = [];

        const existingIndex = groupedFirstOption[priceKey].findIndex((item) => item.value === option1);

        if (existingIndex === -1) {
          groupedFirstOption[priceKey].push({
            value: option1,
            image,
            available: isAvailable,
            price,
            compare_at_price,
            ...(bisData.length ? { bis: true, bisDate: bisData[0].split('[')[1].replace(']', '') } : {}),
          });
        } else if (isAvailable) {
          groupedFirstOption[priceKey][existingIndex].available = true;
        }
      }
    });

    handleFirstLevelSelectedOption(groupedFirstOption);
    return groupedFirstOption;
  };

  const getDiscountedPrice = (price, compareAtPrice) => {
    return discountPriceLogic(compareAtPrice, price);
  };

  const getActualPrice = (price, compareAtPrice) => {
    if(compareAtPrice > 0) {
      return (compareAtPrice);
    } else {
      return price;
    }
  };

  const discountPriceLogic = (compareAtPrice, price) => {
    if(compareAtPrice > 0) {
      if(compareAtPrice * (1 - customerDiscountVal.value) < price) {
        return ((compareAtPrice) * (1 - customerDiscountVal.value)).toFixed(2);
      } else {
        return (price);
      }
    } else {
      return ((price) * (1 - customerDiscountVal.value)).toFixed(2);
    }
  }

  // Helper function to handle remaining options logic
  const generateRemainingOptions = (remainingOptionNames, modifiedSelectedOption, filteredVariants) => {
    const groupedOptions = {};
    remainingOptionNames.forEach((optionName, index) => {
      const optionIndex = index + 1;

      // Get the selected value for this option
      const selectedValue = modifiedSelectedOption[optionName];
      // Determine the filtered variants based on current selections
      const filteredBySelection = filteredVariants.filter((variant) => {
        const isOption2Selected =
          remainingOptionNames[0] === optionName
            ? !modifiedSelectedOption[remainingOptionNames[1]] ||
              variant.option3 === modifiedSelectedOption[remainingOptionNames[1]]
            : true;

        const isOption3Selected =
          remainingOptionNames[1] === optionName
            ? !modifiedSelectedOption[remainingOptionNames[0]] ||
              variant.option2 === modifiedSelectedOption[remainingOptionNames[0]]
            : true;

        return isOption2Selected && isOption3Selected;
      });

      // Extract unique values for the current optionName from the filtered variants
      const uniqueValues = Array.from(
        new Set(filteredBySelection.map((variant) => variant[`option${optionIndex + 1}`]))
      );

      // Generate groupedOptions for the current optionName
      groupedOptions[optionName] = uniqueValues.map((value) => {
        const isAvailable = filteredBySelection.some(
          (variant) => variant[`option${optionIndex + 1}`] === value && variant.available
        );
        return {
          value,
          available: isAvailable,
        };
      });
    });

    return groupedOptions;
  };

  // const isBIS = computed(() => {
  //   const firstSelectedOptionValue = selectedOptions.value[firstOptionName.value];
  //   if (!firstSelectedOptionValue) return false;

  //   if (firstOptionName.value == 'Color') {
  //     return customizedOptions.value?.[firstOptionName.value]?.some((optWrapperObj) =>
  //       optWrapperObj.options.some((option) => option.value === firstSelectedOptionValue && option.bis)
  //     );
  //   } else {
  //     return customizedOptions.value?.[firstOptionName.value]?.some(
  //       (option) => option.value === firstSelectedOptionValue && option.bis
  //     );
  //   }
  // });

    // Update the isBIS computed property to handle the new structure
  const isBIS = computed(() => {
    const firstSelectedOptionValue = selectedOptions.value[firstOptionName.value];
    if (!firstSelectedOptionValue) return false;

    if (firstOptionName.value == 'Color') {
      return customizedOptions.value?.[firstOptionName.value]?.some((optWrapperObj) => {
        // Handle both old structure (price groups) and new structure (edition type groups)
        if (optWrapperObj.editionType) {
          // New edition type structure
          return optWrapperObj.priceGroups?.some(priceGroup => 
            priceGroup.options.some(option => option.value === firstSelectedOptionValue && option?.bis)
          );
        } else {
          // Old price group structure
          return optWrapperObj.options?.some(option => option.value === firstSelectedOptionValue && option?.bis);
        }
      });
    } else {
      return customizedOptions.value?.[firstOptionName.value]?.some(
        (option) => option.value === firstSelectedOptionValue && option?.bis
      );
    }
  });

  const updateSelection = (optionName, value, optGroup) => {
    const optionIndex = product.value.options.findIndex((opt) => opt.name === optionName);
    emit('on-option-change', optionName, value, optionIndex, product.value.options.length);
  };

  // Debounced store update function to avoid infinite call when multiple time firstoption is changing
  const updateFirstLevelOptionsInStore = debounce((firstLevelOptionsObject) => {
    console.log('updateFirstLevelOptionsInStore called!!');
    store.dispatch('updatePDPFirstLevelOptions', {
      ...store.state.pdp.firstLevelOptions,
      [product.value.id]: firstLevelOptionsObject,
    });
  }, 0);

  const handleFirstLevelSelectedOption = (firstLevelOptions) => {
    const data = Object.values(firstLevelOptions).flat();
    const selectedFirstOptionValue = selectedOptions.value[firstOptionName.value];

    // Select the relevant first-level option
    const firstLevelOptionsObject = selectedFirstOptionValue
      ? data.find((option) => option.value === selectedFirstOptionValue)
      : data[0];

    updateFirstLevelOptionsInStore(firstLevelOptionsObject);
  };

  const getModifiedOptionName = (key, value) => {
    return key === 'Size' ? window.formattedSize(value) : value;
  };

  const handleCustomerDiscount = () => {
    customerDiscountVal.value = window.customerDiscount(product.value.tags);
  };

  const formatPrice = (value) => {
    return window.formattedPrice(value);
  };

  const seamHandle = (property, seam) => {
    const decodeProperty = property.replace(/\./g, '-').replace(/[^a-zA-Z0-9-]/g, '');
    const replaceProperty = '-' + seam.replace(/\./g, '-').replace(/[^a-zA-Z0-9-]/g, '');
    const basedHandle = product.value.handle.replace(replaceProperty.toLowerCase(), '');

    return '/products/' + basedHandle + '-' + decodeProperty.toLowerCase();
  };

  const getClassForOption = (optionName) => {
    return {
      'is--color': optionName === 'Color',
      'is--title': optionName === 'Title',
      'is--size': optionName === 'Size',
      'is--other': !['Size', 'Color', 'Title'].includes(optionName),
      'is--additional-options': !['Size', 'Color', 'Title'].includes(optionName) && additionalPropertyOptions.value.length,
    };
  };

  const refreshSelectedOptions = (variants, options) => {
    options.forEach((optName, index) => {
      const optionFound = variants.find((vrnt) => vrnt[`option${index + 2}`] === selectedOptions.value?.[optName]);
      if (!optionFound) {
        delete selectedOptions.value[optName];
      }
    });
  };

  const toggleSizeDrawer = () => {
    window.toggleDrawer(true, 'SizeGuideNew',
      {
        'product': product.value,
        'sizeguide': sizeGuideJson.value,
        'fitType': product.value?.fit_info?.fit_type || '',
        'heading': 'Size Guide'
      }
    )
  };

  onUnmounted(() => {
    updateFirstLevelOptionsInStore.cancel();
  });
</script>
